/**
 * frontend/src/modules/phenomena/lib/aggregations.ts
 *
 * Agrégations client-side V4.20 — modèle 3 axes (Ampleur · Fiabilité · Sens).
 * La "sévérité" composite legacy reste sur le DTO mais n'est plus utilisée ici.
 */

import type {
  Phenomenon,
  Ampleur,
  Fiabilite,
  Sens,
  Nature,
  PatternType,
} from '../types/phenomena.types'
import type { CleI18n } from '@i18n'

export const AMPLEURS_ORDER: Ampleur[] = ['MAJEUR', 'MODERE', 'MINEUR']
export const FIABILITES_ORDER: Fiabilite[] = ['CONFIRME', 'A_CONFIRMER']
export const SENS_ORDER: Sens[] = ['DEFAVORABLE', 'FAVORABLE', 'NEUTRE']

// ── V4.22 / O2 — Famille thématique KPI (MARCHE) ────────────────────────────
// Validée Hkm 2026-06-08 :
//   - Triade PDM lue ensemble (K01/K02/K03 — volume/valeur/stock).
//   - K14 Rotation en Stock & ruptures (ratio écoulement/stock, sémantique stock).
//   - V&P = trio pur K08/K09/K10 (Vol Samsung / CA / ASP).
//   - K28 (perte de distribution) classé Distribution avec K04/K05.
//   - K15-K19, K23 = OPS → hors scope MARCHE (module SFM Team Perf séparé).

export type KpiFamily =
  | 'PDM'              // Part de marché
  | 'DISTRIBUTION'
  | 'VISIBILITE'       // Visibilité étagère
  | 'VOLUMES_PRIX'
  | 'STOCK_RUPTURES'
  | 'CONCURRENCE'
  | 'ACTIVITES_TERRAIN'

export const KPI_FAMILIES_ORDER: KpiFamily[] = [
  'PDM', 'DISTRIBUTION', 'VISIBILITE', 'VOLUMES_PRIX',
  'STOCK_RUPTURES', 'CONCURRENCE', 'ACTIVITES_TERRAIN',
]

// ⚠ `#804` i18n — clés de catalogue résolues au rendu (`useT`), plus de libellé
//   en dur. Le FR reste « Part de marché »… (Δ=0), l'EN bascule.
export const KPI_FAMILY_CLE: Record<KpiFamily, CleI18n> = {
  PDM:                'commun.partMarche',
  DISTRIBUTION:       'phenomenes.familleDistribution',
  VISIBILITE:         'phenomenes.familleVisibilite',
  VOLUMES_PRIX:       'phenomenes.familleVolumesPrix',
  STOCK_RUPTURES:     'phenomenes.familleStock',
  CONCURRENCE:        'phenomenes.familleConcurrence',
  ACTIVITES_TERRAIN:  'phenomenes.familleActivites',
}

/** Mapping kpi → famille thématique (KPIs MARCHE uniquement). */
const KPI_TO_FAMILY: Record<string, KpiFamily> = {
  K01: 'PDM', K02: 'PDM', K03: 'PDM',
  K04: 'DISTRIBUTION', K05: 'DISTRIBUTION', K28: 'DISTRIBUTION',
  K06: 'VISIBILITE', K07: 'VISIBILITE',
  K08: 'VOLUMES_PRIX', K09: 'VOLUMES_PRIX', K10: 'VOLUMES_PRIX',
  K11: 'STOCK_RUPTURES', K12: 'STOCK_RUPTURES', K13: 'STOCK_RUPTURES', K14: 'STOCK_RUPTURES',
  K24: 'CONCURRENCE', K25: 'CONCURRENCE', K26: 'CONCURRENCE', K27: 'CONCURRENCE',
  K20: 'ACTIVITES_TERRAIN', K21: 'ACTIVITES_TERRAIN', K22: 'ACTIVITES_TERRAIN',
}

/** Renvoie la famille thématique du KPI, ou `undefined` si OPS / inconnue. */
export function kpiFamily(kpi: string): KpiFamily | undefined {
  return KPI_TO_FAMILY[kpi]
}

/** Filtre client-side sur nature (MARCHE vs OPS) — V4.20 backend expose `nature`. */
export function filterByNature(phenomena: Phenomenon[], nature: Nature): Phenomenon[] {
  return phenomena.filter((p) => p.nature === nature)
}

// ── D-PHENOMENA-DOUBLONS-PATTERNS (#329) — dédup par phenomenon_group_id ────
//
// Le backend pose `groupId` (post-hook, cf DECISIONS #329) sur les phénomènes
// qui décrivent le MÊME mouvement métier vu par plusieurs patterns (ex :
// « Perte de distribution · 9 PDV » vu par record + streak×2 + acceleration +
// zscore = 5 lignes en base pour 1 seul fait). Sans ce dédup, les surfaces
// cartes (situations critiques, listes Modéré/Mineur, matrice) affichent 5
// cartes identiques au lieu d'1 carte confirmée par 5 méthodes.
//
// N'affecte PAS `DetectionPatterns` (section "Patterns de détection") qui
// compte volontairement les occurrences BRUTES par algorithme — c'est une vue
// signal, pas une vue fait métier.

export interface DedupedPhenomenon extends Phenomenon {
  /** Tous les patternType distincts ayant détecté ce même fait (ordre stable). */
  groupSignatures: PatternType[]
  /** Nombre de lignes brutes fusionnées dans cette carte (1 = pas de doublon). */
  groupSize:       number
}

const AMPLEUR_RANK: Record<Ampleur, number> = { MAJEUR: 0, MODERE: 1, MINEUR: 2 }
const FIAB_RANK:    Record<Fiabilite, number> = { CONFIRME: 0, A_CONFIRMER: 1 }
const PATTERN_ORDER: PatternType[] = ['record', 'streak', 'zscore', 'acceleration', 'yoy']

/**
 * Fusionne les phénomènes partageant le même `groupId` en 1 entrée
 * représentative (la plus significative : ampleur puis fiabilité puis date
 * desc), enrichie de `groupSignatures`/`groupSize`. Les phénomènes sans
 * `groupId` (legacy, jamais re-détectés depuis #329) restent inchangés
 * (groupSize=1, groupSignatures=[patternType]).
 */
export function dedupeByGroupId(phenomena: Phenomenon[]): DedupedPhenomenon[] {
  const groups = new Map<string, Phenomenon[]>()
  const singles: Phenomenon[] = []

  for (const p of phenomena) {
    if (!p.groupId) { singles.push(p); continue }
    if (!groups.has(p.groupId)) groups.set(p.groupId, [])
    groups.get(p.groupId)!.push(p)
  }

  const out: DedupedPhenomenon[] = singles.map((p) => ({
    ...p,
    groupSignatures: [p.patternType],
    groupSize:       1,
  }))

  for (const members of groups.values()) {
    const sorted = members.slice().sort((a, b) => {
      const aA = AMPLEUR_RANK[a.ampleur] ?? 99
      const bA = AMPLEUR_RANK[b.ampleur] ?? 99
      if (aA !== bA) return aA - bA
      const aF = FIAB_RANK[a.fiabilite] ?? 99
      const bF = FIAB_RANK[b.fiabilite] ?? 99
      if (aF !== bF) return aF - bF
      return new Date(b.detectedAt).getTime() - new Date(a.detectedAt).getTime()
    })
    const rep = sorted[0]!
    const distinctPatterns = new Set(members.map((m) => m.patternType))
    const signatures = PATTERN_ORDER.filter((pt) => distinctPatterns.has(pt))
    out.push({ ...rep, groupSignatures: signatures, groupSize: members.length })
  }

  return out
}

/**
 * ⚠⚠ `#689` — **LA MATRICE AMPLEUR × FIABILITÉ EST SUPPRIMÉE, PAS VIDÉE**
 *   (`#658`). *Le composant `CriticityMatrix` n'était monté **nulle part** —
 *   seul un banc le référençait. Ses trois aides (`CriticityCell`,
 *   `CriticityMatrixCounts`, `buildCriticityMatrix`, `rowTotal`) n'avaient **pas
 *   d'autre appelant** : les laisser aurait remplacé un code mort par quatre
 *   exports morts.*
 * ⚠ **DÉCISION HKM** (31/08), prise au vu du `T0` du `P5` (`#688`).
 */

// ── Sélection "situations critiques" V4.20 ──────────────────────────────────
// Public : tout phénomène fiabilité=CONFIRMÉ (signalStrength=FRANC) — équivalent
// de l'ancien CRITIQUE+ELEVEE+FORCE composite. Tri : MAJEUR → MODERE → MINEUR,
// puis défavorable d'abord, puis date desc.
//
// V4.20.1 (Hkm 2026-06-08) : Faits Marquants = MAJEUR + MODÉRÉ, TOUTES fiabilités.
// Les MINEUR restent visibles dans leur tableau dédié plus bas, mais pas dans
// les Faits Marquants. La fiabilité reste affichée sur chaque card via le
// FiabiliteTag — elle pilote le tri, plus le filtre.
//
// Tri : ampleur (MAJEUR→MODÉRÉ) · fiabilité (CONFIRME→A_CONFIRMER) ·
//       sens (DÉFAVORABLE→FAVORABLE→NEUTRE) · date desc.
export function selectCriticalSituations<T extends Phenomenon>(phenomena: T[]): T[] {
  const ampleurRank: Record<Ampleur, number> = { MAJEUR: 0, MODERE: 1, MINEUR: 2 }
  const fiabRank: Record<string, number> = { CONFIRME: 0, A_CONFIRMER: 1 }
  const sensRank: Record<Sens, number> = { DEFAVORABLE: 0, FAVORABLE: 1, NEUTRE: 2 }
  return phenomena
    .filter((p) => p.ampleur !== 'MINEUR')
    .sort((a, b) => {
      const aA = ampleurRank[a.ampleur] ?? 99
      const bA = ampleurRank[b.ampleur] ?? 99
      if (aA !== bA) return aA - bA
      const aF = fiabRank[a.fiabilite] ?? 99
      const bF = fiabRank[b.fiabilite] ?? 99
      if (aF !== bF) return aF - bF
      const aS = sensRank[a.sens] ?? 99
      const bS = sensRank[b.sens] ?? 99
      if (aS !== bS) return aS - bS
      return new Date(b.detectedAt).getTime() - new Date(a.detectedAt).getTime()
    })
}

/** Groupe par AMPLEUR (ordre MAJEUR → MODERE → MINEUR). */
export function groupByAmpleur<T extends Phenomenon>(phenomena: T[]): Record<Ampleur, T[]> {
  const groups: Record<Ampleur, T[]> = {
    MAJEUR: [], MODERE: [], MINEUR: [],
  }
  for (const p of phenomena) groups[p.ampleur]?.push(p)
  return groups
}

// ── Groupes par pattern (inchangé — basés sur ampleur×fiabilité pour le tri) ─

export interface PatternGroup {
  patternType: PatternType
  count:       number
  items:       Phenomenon[]
}

export function groupByPattern(phenomena: Phenomenon[]): PatternGroup[] {
  const map = new Map<PatternType, Phenomenon[]>()
  for (const p of phenomena) {
    if (!map.has(p.patternType)) map.set(p.patternType, [])
    map.get(p.patternType)!.push(p)
  }
  // Tri V4.20 : MAJEUR+CONFIRME d'abord, défavorable prioritaire, puis date desc.
  const ampleurRank: Record<Ampleur, number> = { MAJEUR: 0, MODERE: 1, MINEUR: 2 }
  const fiabiliteRank: Record<Fiabilite, number> = { CONFIRME: 0, A_CONFIRMER: 1 }
  const sensRank: Record<Sens, number> = { DEFAVORABLE: 0, FAVORABLE: 1, NEUTRE: 2 }
  const all: PatternType[] = ['record', 'streak', 'acceleration', 'zscore', 'yoy']
  return all.map((pt) => {
    const items = (map.get(pt) ?? []).slice().sort((a, b) => {
      const aA = ampleurRank[a.ampleur] ?? 99
      const bA = ampleurRank[b.ampleur] ?? 99
      if (aA !== bA) return aA - bA
      const aF = fiabiliteRank[a.fiabilite] ?? 99
      const bF = fiabiliteRank[b.fiabilite] ?? 99
      if (aF !== bF) return aF - bF
      const aS = sensRank[a.sens] ?? 99
      const bS = sensRank[b.sens] ?? 99
      if (aS !== bS) return aS - bS
      return new Date(b.detectedAt).getTime() - new Date(a.detectedAt).getTime()
    })
    return { patternType: pt, count: items.length, items }
  })
}

// ── V4.22 / O2 — Agrégations cockpit Overview ──────────────────────────────

/** Groupe par KPI (clé = code KPI). Ordre = ordre d'apparition. */
export function groupByKpi(phenomena: Phenomenon[]): Record<string, Phenomenon[]> {
  const out: Record<string, Phenomenon[]> = {}
  for (const p of phenomena) {
    if (!out[p.kpi]) out[p.kpi] = []
    out[p.kpi]!.push(p)
  }
  return out
}

/** Groupe par famille thématique. KPIs hors mapping (OPS) → ignorés. */
export function groupByKpiFamily(phenomena: Phenomenon[]): Record<KpiFamily, Phenomenon[]> {
  const out: Record<KpiFamily, Phenomenon[]> = {
    PDM: [], DISTRIBUTION: [], VISIBILITE: [], VOLUMES_PRIX: [],
    STOCK_RUPTURES: [], CONCURRENCE: [], ACTIVITES_TERRAIN: [],
  }
  for (const p of phenomena) {
    const f = kpiFamily(p.kpi)
    if (f) out[f].push(p)
  }
  return out
}

/** Une ligne du rollup régions. */
export interface RegionRollupRow {
  regionKey:                string         // UUID region
  label:                    string | null  // nom de région (ou null si inconnu)
  occurrences:              number          // nb de phénomènes où la région apparaît
  sumAbsContributionPct:    number          // Σ |contributionPct| sur tous les phénomènes
}

/**
 * Rollup régions des phénomènes filtrés (par défaut MAJEUR uniquement).
 * Lit `detail.decompositions.GEO.contributors[]` pré-calculé par le post-hook
 * backend — GRATUIT, aucune requête supplémentaire.
 *
 * Sortie triée par `sumAbsContributionPct DESC` puis `occurrences DESC`.
 * Ignore le contributeur agrégé `__OTHERS__`.
 */
export function rollupRegionsFromDetail(
  phenomena: Phenomenon[],
  opts: { ampleurs?: Ampleur[] } = {},
): RegionRollupRow[] {
  const ampleurFilter = opts.ampleurs
  const acc = new Map<string, RegionRollupRow>()

  for (const p of phenomena) {
    if (ampleurFilter && !ampleurFilter.includes(p.ampleur)) continue
    const geo = p.detail?.decompositions?.GEO
    const contribs = geo?.contributors ?? []
    for (const c of contribs) {
      const raw = c as unknown as Record<string, unknown>
      const key =
        (typeof raw.key === 'string' && raw.key) ||
        (typeof raw.dimensionKey === 'string' && (raw.dimensionKey as string)) ||
        (typeof raw.regionId === 'string' && (raw.regionId as string)) ||
        null
      if (!key || key === '__OTHERS__') continue
      const lbl =
        (typeof raw.label === 'string' && (raw.label as string)) ||
        (typeof raw.dimensionLabel === 'string' && (raw.dimensionLabel as string)) ||
        (typeof raw.regionName === 'string' && (raw.regionName as string)) ||
        null
      const pctRaw = raw.contributionPct ?? raw.contribution ?? 0
      const pct = typeof pctRaw === 'number' ? Math.abs(pctRaw) : 0
      const existing = acc.get(key)
      if (existing) {
        existing.occurrences           += 1
        existing.sumAbsContributionPct += pct
        if (!existing.label && lbl) existing.label = lbl
      } else {
        acc.set(key, { regionKey: key, label: lbl, occurrences: 1, sumAbsContributionPct: pct })
      }
    }
  }

  return [...acc.values()].sort((a, b) => {
    if (b.sumAbsContributionPct !== a.sumAbsContributionPct) {
      return b.sumAbsContributionPct - a.sumAbsContributionPct
    }
    return b.occurrences - a.occurrences
  })
}
